import { useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { fromLocalInput, offsetMinutes } from '../../utils/specimenCode';
import { can } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import { currentFix } from './SpecimenOnSite';
import { LAB_CODE_PATTERN } from './LabCodeEditor';
import * as S from './styles';

/*
 * Register a specimen with everything known about it -- now, or after the
 * fact from a notebook, a herbarium sheet or an existing collection.
 *
 * Retroactive entry mints the code from the HISTORICAL date, time and place,
 * exactly as if it had been minted then; only the date is needed (a date
 * alone means "time unknown" and gives HHMM 0000). An existing collection or
 * intake, found by its code, supplies whatever is left blank.
 *
 * One origin has one isolate: a collection, intake or strain that already has
 * a specimen (Field Mode mints one, and the server mints one on submit) is
 * refused with 409 origin_registered. The form says so in the reader's
 * language, naming the existing code when the server gave it (only to whoever
 * may read it), and that specimen can be opened from here (onOpen) rather
 * than registered twice.
 */
const empty = {
  mode: 'retroactive', when: '', dateOnly: false, location_code: '', gps_lat: '', gps_lng: '',
  location_name: '', taxon_name: '', collector_name: '', notes: '', origin_code: '', lab_code: '',
};

export default function SpecimenRegisterForm({ t, onCreated, onOpen }) {
  const { user } = useAuth() || {};
  // The lab may give a new isolate the code its tube in the bank already
  // carries (0083); otherwise the database numbers it.
  const mayCode = can(user, 'specimen:write');
  const [form, setForm] = useState(empty);
  const [origin, setOrigin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState(null);   // the specimen a 409 origin_registered named
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const findOrigin = async () => {
    setError(''); setOrigin(null); setExisting(null);
    const code = form.origin_code.trim();
    if (!code) return;
    try {
      const res = await api.get('/lookup', { params: { code } });
      if (res.data.type === 'collection') setOrigin({ sample_id: res.data.sample_id, code: res.data.code, kind: 'collection' });
      else if (res.data.type === 'external') setOrigin({ external_id: res.data.external_id, code: res.data.code, kind: 'external' });
      // A line with no collection behind it (a primary isolate): the specimen
      // is linked to its strain, and becomes its slants' Isolate ID.
      else if (res.data.type === 'strain') setOrigin({ strain_id: res.data.strain_id, code: res.data.code, kind: 'strain' });
      else setError(t('register.originWrongType'));
    } catch (e) {
      setError(e.response?.status === 404 ? t('register.originNotFound') : errorText(e));
    }
  };

  const locate = async () => {
    const fix = await currentFix();
    if (fix) setForm((f) => ({ ...f, gps_lat: String(fix.lat), gps_lng: String(fix.lng) }));
    else setError(t('register.noFix'));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setExisting(null);
    const body = { mode: form.mode, utc_offset_minutes: offsetMinutes() };
    if (form.mode === 'retroactive') {
      const raw = form.dateOnly ? form.when.slice(0, 10) : form.when;
      const when = fromLocalInput(raw);
      // A collection or an intake knows when it was collected; a strain does not.
      if (!when && (!origin || origin.kind === 'strain')) { setError(t('register.whenRequired')); return; }
      if (when) {
        body.collected_at = when;
        // The offset that applied on that date, not today's.
        body.utc_offset_minutes = offsetMinutes(new Date(form.dateOnly ? `${raw}T12:00` : raw));
      }
    }
    if (form.gps_lat !== '' || form.gps_lng !== '') {
      body.gps_lat = form.gps_lat === '' ? null : Number(form.gps_lat);
      body.gps_lng = form.gps_lng === '' ? null : Number(form.gps_lng);
    }
    ['location_code', 'location_name', 'taxon_name', 'collector_name', 'notes'].forEach((key) => {
      if (form[key].trim()) body[key] = form[key].trim();
    });
    if (origin?.sample_id) body.sample_id = origin.sample_id;
    if (origin?.external_id) body.external_id = origin.external_id;
    if (origin?.strain_id) body.strain_id = origin.strain_id;
    if (mayCode && form.lab_code.trim()) {
      if (!LAB_CODE_PATTERN.test(form.lab_code.trim())) { setError(t('labCodeEdit.invalid')); return; }
      body.lab_code = form.lab_code.trim();
    }
    setBusy(true);
    try {
      const res = await api.post('/specimens', body);
      setForm(empty); setOrigin(null);
      onCreated?.(res.data);
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 && data.code === 'origin_registered') {
        // Said in the reader's language, not the server's English: the server
        // sends specimen_code only to a caller who may see that isolate.
        setError(data.specimen_code
          ? t('register.originRegisteredNamed', { code: data.specimen_code })
          : t('register.originRegistered'));
        if (data.specimen_id) {
          setExisting({ specimen_id: data.specimen_id, specimen_code: data.specimen_code, lab_code: data.lab_code });
        }
      } else if (data.code === 'lab_code_taken') {
        setError(t('labCodeEdit.taken', { code: data.lab_code, holder: data.holder }));
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const retro = form.mode === 'retroactive';
  return (
    <form onSubmit={submit} style={S.card} data-testid="specimen-register" aria-labelledby="register-title">
      <h3 id="register-title" style={S.sectionTitle}>{t('register.title')}</h3>
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 10px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <legend style={{ ...S.hint, marginBottom: 6 }}>{t('register.modeLegend')}</legend>
        {['retroactive', 'on_site'].map((mode) => (
          <label key={mode} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            <input type="radio" name="mode" value={mode} checked={form.mode === mode} onChange={set('mode')}
              data-testid={`register-mode-${mode}`} title={t(`register.tip.${mode}`)} />
            {t(`register.modes.${mode}`)}
          </label>
        ))}
      </fieldset>
      <p style={S.hint}>{retro ? t('register.retroHint') : t('register.nowHint')}</p>

      <div style={S.row}>
        <label style={{ ...S.label, flex: '2 1 220px' }}>
          {t('register.originCode')}
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={form.origin_code} onChange={set('origin_code')} dir="ltr" style={{ ...S.input, flex: 1 }}
              title={t('register.tip.origin')} data-testid="register-origin" />
            <button type="button" onClick={findOrigin} style={S.ghostBtn} title={t('register.tip.findOrigin')}
              data-testid="register-find-origin">{t('register.findOrigin')}</button>
          </div>
          {origin && (
            <span style={{ ...S.hint, color: 'var(--c-success-fg)' }} data-testid="register-origin-found">
              {t(`register.originFound.${origin.kind}`, { code: origin.code })}
            </span>
          )}
        </label>
      </div>

      {retro && (
        <div style={{ ...S.row, marginTop: 10 }}>
          <label style={{ ...S.label, flex: '1 1 220px' }}>
            {t('register.when')}
            <input type={form.dateOnly ? 'date' : 'datetime-local'} value={form.dateOnly ? form.when.slice(0, 10) : form.when}
              onChange={set('when')} style={S.input} title={t('register.tip.when')} data-testid="register-when" />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, paddingBottom: 8 }}>
            <input type="checkbox" checked={form.dateOnly} onChange={set('dateOnly')} data-testid="register-date-only"
              title={t('register.tip.dateOnly')} />
            {t('register.dateOnly')}
          </label>
        </div>
      )}

      <div style={{ ...S.row, marginTop: 10 }}>
        <label style={{ ...S.label, flex: '1 1 130px' }}>
          {t('fields.siteCode')}
          <input value={form.location_code} onChange={set('location_code')} maxLength={12} dir="ltr"
            style={{ ...S.input, ...S.mono, textTransform: 'uppercase' }} title={t('tip.siteCode')}
            data-testid="register-site" />
        </label>
        <label style={{ ...S.label, flex: '1 1 120px' }}>
          {t('fields.lat')}
          <input type="number" step="any" min="-90" max="90" value={form.gps_lat} onChange={set('gps_lat')} dir="ltr"
            style={S.input} title={t('tip.lat')} data-testid="register-lat" />
        </label>
        <label style={{ ...S.label, flex: '1 1 120px' }}>
          {t('fields.lng')}
          <input type="number" step="any" min="-180" max="180" value={form.gps_lng} onChange={set('gps_lng')} dir="ltr"
            style={S.input} title={t('tip.lng')} data-testid="register-lng" />
        </label>
        <button type="button" onClick={locate} style={S.ghostBtn} title={t('register.tip.locate')}>
          {t('register.locate')}
        </button>
      </div>
      <p style={S.hint}>{t('fields.siteCodeHint')}</p>

      <div style={{ ...S.row, marginTop: 6 }}>
        <label style={{ ...S.label, flex: '2 1 200px' }}>
          {t('fields.locationName')}
          <input value={form.location_name} onChange={set('location_name')} maxLength={300} style={S.input}
            title={t('tip.locationName')} />
        </label>
        <label style={{ ...S.label, flex: '2 1 200px' }}>
          {t('fields.taxon')}
          <input value={form.taxon_name} onChange={set('taxon_name')} maxLength={300} style={S.input}
            title={t('tip.taxon')} data-testid="register-taxon" />
        </label>
        <label style={{ ...S.label, flex: '1 1 160px' }}>
          {t('fields.collector')}
          <input value={form.collector_name} onChange={set('collector_name')} maxLength={200} style={S.input}
            title={t('tip.collector')} />
        </label>
        {mayCode && (
          <label style={{ ...S.label, flex: '1 1 160px' }}>
            {t('register.labCode')}
            <input value={form.lab_code} onChange={set('lab_code')} maxLength={40} dir="ltr"
              style={{ ...S.input, ...S.mono }} title={t('register.tip.labCode')} data-testid="register-lab-code" />
          </label>
        )}
      </div>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('fields.notes')}
        <textarea value={form.notes} onChange={set('notes')} rows={2} maxLength={4000} style={S.input}
          title={t('tip.notes')} />
      </label>
      {error && <p style={S.error} role="alert" data-testid="register-error">{error}</p>}
      {existing && onOpen && (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => onOpen(existing.specimen_id)} style={S.ghostBtn}
            title={t('register.tip.openExisting')} data-testid="register-open-existing">
            {t('register.openExisting')}
            {[existing.specimen_code, existing.lab_code].filter(Boolean).map((code) => (
              <span key={code}> · <bdi dir="ltr" style={S.mono}>{code}</bdi></span>
            ))}
          </button>
        </div>
      )}
      <button type="submit" disabled={busy} style={{ ...S.btn(), marginTop: 12 }} title={t('register.tip.submit')}
        data-testid="register-submit">{busy ? t('register.submitting') : t('register.submit')}</button>
    </form>
  );
}
